import React, { useState } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Download, FileText } from 'lucide-react';
import { message } from 'antd';


function BulkProductUploadModal({ isOpen, onClose, onUploadSuccess }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  if (!isOpen) return null;

  const handleDownloadTemplate = async () => {
    try {
      message.loading({ content: 'Preparing template...', key: 'download' });
      setTimeout(() => {
        message.success({ content: 'Template download started locally!', key: 'download' });
      }, 500);
    } catch (err) {
      console.error('Error downloading template:', err);
      message.error({ content: 'Failed to download template', key: 'download' });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      const isValidType = validExtensions.some(ext => fileName.endsWith(ext));

      if (!isValidType) {
        message.warning('Please upload a valid CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.warning('File size must be less than 10MB');
        return;
      }

      setUploadedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = handleFileChange;
    input.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadResult(null);
  };

  const handleSave = async () => {
    if (!uploadedFile) {
      message.warning('Please upload a file first');
      return;
    }

    setUploading(true);
    setTimeout(() => {
      try {
        const result = {
          success: true,
          message: 'Successfully uploaded products (Mocked)!',
          totalProcessed: 1,
          successCount: 1,
          failureCount: 0,
          failedProducts: []
        };

        setUploadResult(result);

        if (onUploadSuccess) {
          onUploadSuccess(result);
        }

        message.success('Upload completed successfully');
        if (result.failureCount === 0) {
          setTimeout(() => handleClose(), 3000);
        }
      } catch (err) {
        console.error('Error bulk uploading products:', err);
        setUploadResult({
          success: false,
          message: err.message || 'Failed to upload products',
          totalProcessed: 0,
          successCount: 0,
          failureCount: 0,
          failedProducts: [],
        });
        message.error('Bulk upload failed');
      } finally {
        setUploading(false);
      }
    }, 1000);
  };

  const handleClose = () => {
    setUploadedFile(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#E8F5F0] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#E8F5F0] z-10 p-6 pb-4 border-b border-teal-200">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Add Bulk Products</h2>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Upload Result Alert */}
          {uploadResult && (
            <div className={`mb-6 p-4 rounded-lg border-2 ${uploadResult.success
              ? 'bg-green-50 border-green-300'
              : 'bg-red-50 border-red-300'
              }`}>
              <div className="flex items-start space-x-3">
                {uploadResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4 className={`font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                    {uploadResult.message}
                  </h4>

                  {uploadResult.totalProcessed > 0 && (
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-600 mb-1">Total Processed</p>
                        <p className="text-lg font-bold text-gray-800">{uploadResult.totalProcessed}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Successful</p>
                        <p className="text-lg font-bold text-green-600">{uploadResult.successCount}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-red-200">
                        <p className="text-xs text-gray-600 mb-1">Failed</p>
                        <p className="text-lg font-bold text-red-600">{uploadResult.failureCount}</p>
                      </div>
                    </div>
                  )}

                  {uploadResult.failedProducts && uploadResult.failedProducts.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-red-800 mb-2">
                        Failed Products ({uploadResult.failedProducts.length}):
                      </p>
                      <div className="bg-white rounded-lg border border-red-200 max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-red-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 font-semibold text-red-800">Row</th>
                              <th className="text-left p-2 font-semibold text-red-800">Product</th>
                              <th className="text-left p-2 font-semibold text-red-800">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.failedProducts.slice(0, 10).map((failed, index) => (
                              <tr key={index} className="border-t border-red-100">
                                <td className="p-2 text-gray-700">{failed.row}</td>
                                <td className="p-2 text-gray-700">{failed.productName || 'N/A'}</td>
                                <td className="p-2 text-red-700">{failed.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {uploadResult.failedProducts.length > 10 && (
                          <div className="p-2 text-center text-xs text-red-600 bg-red-50 border-t border-red-100">
                            ...and {uploadResult.failedProducts.length - 10} more errors
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="space-y-6">
            {/* Step 1 - Download Template */}
            <div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm">
                  <span className="text-teal-600 font-bold">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-1">
                    Please download Excel file template.
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Choose and download the template and fill in your products data.
                  </p>
                  <button
                    onClick={handleDownloadTemplate}
                    className="px-6 py-2.5 bg-white border-2 border-teal-500 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors flex items-center space-x-2 font-medium shadow-sm"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download Template</span>
                  </button>

                  {/* Template Info */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <FileText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-blue-800">
                        <p className="font-semibold mb-1">Template includes:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                          <li>Required fields: SKU, Product Name, Category, Price</li>
                          <li>Optional fields: Description, MRP, Stock Quantity, HSN Code</li>
                          <li>Pre-filled sample product rows</li>
                          <li>Format: Excel (.xlsx)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 - Upload File */}
            <div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm">
                  <span className="text-teal-600 font-bold">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-1">
                    Please upload updated Excel file template.
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Please upload filled data here.
                  </p>

                  {/* Upload Area */}
                  {!uploadedFile ? (
                    <div
                      onClick={handleUpload}
                      className="border-2 border-dashed border-teal-300 rounded-xl p-8 bg-white cursor-pointer hover:border-teal-500 hover:bg-teal-50 transition-all"
                    >
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center">
                          <Upload className="w-8 h-8 text-teal-500" />
                        </div>
                        <div className="text-center">
                          <button className="text-teal-600 font-medium hover:text-teal-700">
                            Click to Upload
                          </button>
                          <p className="text-xs text-gray-500 mt-1">
                            CSV, XLSX or XLS (Max 10MB)
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-teal-300 rounded-xl p-4 bg-white shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-teal-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 truncate">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                            {uploading && (
                              <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                                <div
                                  className="h-full bg-teal-500 rounded-full transition-all duration-300"
                                  style={{ width: '75%' }}
                                >
                                  <div className="w-full h-full bg-teal-400 rounded-full animate-pulse"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          disabled={uploading}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-2 disabled:opacity-50"
                        >
                          <X className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#E8F5F0] p-6 pt-4 border-t border-teal-200 flex justify-end">
          <button
            onClick={handleSave}
            disabled={!uploadedFile || uploading}
            className="px-8 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2 shadow-md"
          >
            {uploading && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <span>{uploading ? 'Uploading...' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkProductUploadModal;